<template>
  <div id="top-page">
    <v-container>
      <v-dialog v-if="isSuccessDialogOpened" v-model="isSuccessDialogOpened" persistent max-width="400">
        <v-card>
          <v-card-title class="headline">Site created succefully!</v-card-title>
          <v-card-text>
            <span class="mb-4">Your firebase site ID:</span>
            <v-alert :value="true" color="info" icon="mdi-information" outlined dense>
              <span class="font-weight-bold">{{ siteKey }}</span>
            </v-alert>
            <span>please use this id when you set up a new AMP site.</span>
            <v-row class="justify-center mt-2">
              <v-btn color="grey darken-2" outlined small @click="close">Close</v-btn>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
      <template v-if="siteItem">
        <v-row class="mb-n2">
          <v-btn icon class="px-3 mt-n1 pb-1" @click.native="back()">
            <v-icon color="red">mdi-arrow-left-bold</v-icon>
          </v-btn>
          <span v-if="isOpenedToEdit && siteItem.site_name" class="headline mt-n1">
            Edit:
            <span class="primary--text">{{ siteItem.site_name }}</span>
          </span>
          <span v-else class="headline mt-n1 mb-3">New Site</span>
          <v-spacer></v-spacer>
          <p v-if="isOpenedToEdit" class="body-2 font-weight-medium grey--text text--darken-2 mr-4 pt-1 justify-end"><b>Creation date:</b> {{ formatDate(siteItem.created_at) }}</p>
        </v-row>
        <v-divider></v-divider>
        <v-row class="justify-center mt-3 mb-2 px-3">
          <v-tabs v-model="tab" background-color="grey lighten-5" grow>
            <v-tab><v-icon class="mr-2">mdi-cog</v-icon>General settings</v-tab>
            <v-tab><v-icon class="mr-2">mdi-tune</v-icon>Custom settings</v-tab>
          </v-tabs>
        </v-row>
        <v-alert v-model="siteUpdateAlert" type="success" dismissible>Site updated successfully!</v-alert>
        <!-- GENERAL SETTINGS -->
        <v-card v-if="!tab" width="100%">
          <v-container grid-list-md>
            <v-row class="mx-2">
              <v-col cols="12">
                <v-form ref="form" v-model="isValid">
                  <v-row class="justify-space-between mt-3">
                    <p class="headline ml-3 primary--text">General settings</p>
                    <div>
                      <v-btn color="primary" class="mr-2" @click="onManageProducts"> <v-icon class="pr-1">mdi-plus-box-multiple</v-icon>Manage products</v-btn>
                      <v-btn :disabled="!isValid" color="primary" class="mr-2" @click="submit"> <v-icon class="pr-1">mdi-floppy</v-icon>Save </v-btn>
                    </div>
                  </v-row>
                  <v-row>
                    <v-col cols="6" xl="4">
                      <v-text-field v-model="siteItem.site_name" label="Site name" :rules="SiteNameIdRules" required> </v-text-field>
                      <v-text-field v-model="siteItem.site_id" label="Site ID" :rules="siteIdRules" required> </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="justify-space between">
                    <v-col cols="12" md="6" lg="5" xl="4">
                      <v-row class="pl-3">
                        <v-switch v-model="isCampSite" color="cyan darken-2" :label="`Environment type: ${activeText}`" class="mt-0"> </v-switch>
                      </v-row>

                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.checkout_order_enabled" color="cyan darken-2" label="Checkout Order" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.multi_order_checkout_enabled" color="cyan darken-2" label="Multi Order Checkout" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_edit_order_enabled_on_checkout" color="cyan darken-2" label="Edit Order on Checkout Page" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.order_label_update_enabled" color="cyan darken-2" label=" Edit Order Label" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch
                          v-model="siteItem.credit_card_payment_enabled"
                          color="cyan darken-2"
                          :disabled="isCreditCardDisabled"
                          label="Credit Card Payment"
                          class="mt-0"
                          @click="onCreditCardPaymentChanged"
                        >
                        </v-switch>
                      </v-row>
                      <v-row>
                        <v-alert v-if="isCreditCardDisabled" class="mt-0" type="info" outlined color="#737373">
                          <span>Bill Corporate Account is turned off</span>
                        </v-alert>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_place_order_enabled" color="cyan darken-2" label="Place Order on My Account Tabs" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.starting_order_with_map_enabled" color="cyan darken-2" label="Starting Order With Map" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.download_emap_csv_enabled_on_site" color="cyan darken-2" label="Download CSV from Emap" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.salesforce_opportunity_enabled" color="cyan darken-2" label="Salesforce Opportunity" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.show_order_approval" color="cyan darken-2" label="Order Approval (Gatekeeper):" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.order_duplication_enabled" color="cyan darken-2" label="Order Duplication" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.finalize_design_enabled" color="cyan darken-2" label="Finalize Design" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.edit_automated_mailer_enabled" color="cyan darken-2" label="Edit Automated Mailer" class="mt-0"></v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.corporate_automated_mailer_enabled" color="cyan darken-2" label="Automated Mailer with Bill Corporate" class="mt-0"></v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.text_messages_enabled" color="cyan darken-2" label="Text Messages" class="mt-0"></v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_call_tracking_enabled" color="cyan darken-2" label="Call Tracking" class="mt-0"></v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.email_templates_enabled" color="cyan darken-2" label="Email Templates" class="mt-0"></v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_product_from_db" color="cyan darken-2" :label="productSourceText" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_markup_prices_enabled" color="cyan darken-2" label="Markup Price" class="mt-0"> </v-switch>
                      </v-row>
                    </v-col>
                    <v-col cols="12" md="6" lg="6" xl="5">
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.download_quote_enabled" color="cyan darken-2" label="Enable download quote as PDF" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.postage_auth_number_enabled" color="cyan darken-2" label="Postage - Non Profit Authorization Number" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_promo_enabled" color="cyan darken-2" label="Allow Coupon Usage" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.sftp_upload_enabled" color="cyan darken-2" label="Enable SFTP upload" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.tax_enabled" color="cyan darken-2" label="Tax Price" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.cancel_subscription_enabled" color="cyan darken-2" label="Enable Cancel Subscription" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.pricing_table_enabled" color="cyan darken-2" label="Visible Pricing Table" class="mt-0"> </v-switch>
                        <v-alert v-if="isPricingWarningMessageVisible" type="info" dismissible outlined dense
                          >Note: Please turn off Re-Order Completed Order functionality in order to hide the pricing in all cases.</v-alert
                        >
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.re_order_enabled" color="cyan darken-2" label="Re-Order Completed Orders" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.is_edit_custom_product_enabled" color="cyan darken-2" label="Edit Custom Product" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.fulfilment_location_enabled" color="cyan darken-2" label="Fulfilment Location" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row v-if="!isCampSite" class="pl-3">
                        <v-switch v-model="siteItem.enable_external_quote_saving" color="cyan darken-2" label="External quote saving" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row v-if="!isCampSite && siteItem.enable_external_quote_saving" class="ml-9 mt-n9 mb-n4">
                        <v-col cols="12" md="8">
                          <v-autocomplete
                            v-model="siteItem.sites_for_quote_saving_access"
                            :items="oboppSites"
                            label="OBOPP sites for quote saving access"
                            required
                            item-value="firebase_id"
                            item-text="site_name"
                            multiple
                            chips
                            :error-messages="autocompleteErrorMessage"
                          >
                          </v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.share_quote_enabled" color="cyan darken-2" label="Share Quote" class="mt-0"> </v-switch>
                      </v-row>
                      <v-row v-if="siteItem.share_quote_enabled" class="ml-9 mt-n9 mb-n4">
                        <v-col cols="12" md="8">
                          <v-autocomplete
                            v-model="siteItem.sites_for_share_quote"
                            :items="availableSitesForShare"
                            label="Sites for quote sharing"
                            required
                            item-text="site_name"
                            multiple
                            chips
                            :error-messages="autocompleteErrorMessage"
                            return-object
                          >
                          </v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.register_user_fields_enabled" color="cyan darken-2" label="Extra User Fields" class="mt-0"> </v-switch>
                      </v-row>
                      <template v-if="siteItem.register_user_fields_enabled">
                        <v-row v-for="index in 5" :key="index" class="ml-9 mt-n10 mb-n4">
                          <v-col cols="12" md="8">
                            <v-text-field v-model="siteItem.register_user_fields[index - 1]" :label="`Label: ${index - 1}`"> </v-text-field>
                          </v-col>
                        </v-row>
                      </template>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-0">
                    <v-col cols="12" md="6" lg="5">
                      <v-switch v-model="siteItem.additional_services_available" color="cyan darken-2" label="Additional Services"></v-switch>
                    </v-col>
                    <v-col cols="12" md="6" lg="4">
                      <v-switch v-model="siteItem.additional_services_editing_enabled" color="cyan darken-2" label="Enable Editing Additional Services"></v-switch>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n10 mb-n2">
                    <v-col cols="12" md="6" lg="5">
                      <v-switch v-model="siteItem.manual_discount_available" color="cyan darken-2" label="Manual Discount"></v-switch>
                    </v-col>
                    <v-col cols="12" md="6" lg="4">
                      <v-switch v-model="siteItem.manual_discount_editing_enabled" color="cyan darken-2" label="Enable Editing Manual Discount"></v-switch>
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <p class="title mt-3 grey--text text--darken-2 font-weight-regular">My Account Tabs</p>
                  <v-row class="mb-n1 pt-2">
                    <v-col v-for="(tab, tabIndex) in siteItem.my_account_tabs" :key="tabIndex" cols="12" md="6" lg="5" class="mt-n8 mb-n2">
                      <v-switch
                        v-if="tabIndex === 'orders_enabled'"
                        :key="tabIndex"
                        v-model="siteItem.my_account_tabs[tabIndex]"
                        color="cyan darken-2"
                        :label="removeLastWord(tabIndex) | beautifyText"
                        readonly
                      ></v-switch>
                      <v-switch
                        v-else
                        :key="tabIndex"
                        v-model="siteItem.my_account_tabs[tabIndex]"
                        color="cyan darken-2"
                        :label="removeLastWord(tabIndex) | beautifyText"
                      ></v-switch>
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row>
                    <v-col cols="12">
                      <v-switch v-model="siteItem.user_defined_fields_enabled" color="cyan darken-2" label="Enable User-Defined ORDER Fields" class="pt-2"></v-switch>
                      <v-row v-if="siteItem.user_defined_fields_enabled" class="ml-n1">
                        <v-col cols="10">
                          <v-layout v-for="index in fieldCount" :key="index" align-center>
                            {{ siteItem.user_defined_fields.field_name }}
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-checkbox v-model="siteItem.user_defined_fields[index - 1].field_is_required" class="mr-1" v-on="on"></v-checkbox>
                              </template>
                              <span>Set as required field</span>
                            </v-tooltip>
                            <v-text-field v-model="siteItem.user_defined_fields[index - 1].field_name" :label="`Field Name : ${index - 1}`" class="mr-4"> </v-text-field>
                            <v-text-field v-model="siteItem.user_defined_fields[index - 1].field_label" :label="`Field label: ${index - 1}`" class="mr-4"> </v-text-field>
                            <v-text-field v-model="siteItem.user_defined_fields[index - 1].field_description" :label="`Field Description: ${index - 1}`" class="mr-4">
                            </v-text-field>
                            <v-text-field v-model="siteItem.user_defined_fields[index - 1].field_id" :label="`Field ID: ${index - 1}`" class="mr-4" :disabled="true">
                            </v-text-field>
                            <v-text-field
                              v-model="siteItem.user_defined_fields[index - 1].field_type"
                              :disabled="true"
                              :label="`Field type: ${index - 1}`"
                              class="stamp-site-process-custom-width"
                            >
                            </v-text-field>
                          </v-layout>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-2">
                    <v-col cols="12" md="6">
                      <v-text-field v-model="siteItem.contact_phone" label="Contact Phone"> </v-text-field>
                      <v-text-field v-model="siteItem.brand_web_id" label="Brand web ID" :rules="BrandWebIdRules" required> </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="12" md="6">
                      <v-select v-model="siteItem.site_type" :items="siteTypes" item-text="text" item-value="value" label="Select Site Type"> </v-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6" class="mt-n4">
                      <v-text-field v-model="siteItem.site_url" label="Site url" :rules="SiteUrlRules" required> </v-text-field>
                      <v-text-field v-model="siteItem.site_api_key" label="Site API key" :rules="SiteAPiKeyRules" required> </v-text-field>
                      <v-text-field v-model="siteItem.site_api_username" label="WP-API Auth JWT Username" required> </v-text-field>
                      <v-text-field v-model="siteItem.site_api_password" label="WP-API Auth JWT Password" required> </v-text-field>
                      <v-text-field v-model="siteItem.crm_api_endpoint" label="CRM API Endpoint"> </v-text-field>
                      <v-text-field v-model="siteItem.custom_quote_url" label="Custom Quote URL"> </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="justify-space-between mt-n4">
                    <v-col cols="12" md="6">
                      <v-text-field v-model="siteItem.client_logo" label="Client logo"> </v-text-field>
                    </v-col>
                    <v-col v-if="siteItem.client_logo" cols="12" md="6">
                      <v-row class="justify-center pt-1">
                        <v-img :src="siteItem.client_logo" max-width="280px" alt="client_logo" style="position: absolute"> </v-img>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="12" md="6">
                      <file-pond
                        ref="pond"
                        name="client_logo"
                        label-idle="Drop files here or Browse for Client logo"
                        accepted-file-types="image/jpeg, image/png"
                        :server="{ process }"
                        allowBrowse="true"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="siteItem.design_live_asset_folder_path" label="Design live asset folder path" :rules="DesignLiveRules" required> </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="12" md="6">
                      <v-text-field v-model="siteItem.httpimg_url" label="HttpImg URL"> </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="12" md="6">
                      <v-text-field v-model="siteItem.segment_write_key" label="Segment Write Key"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-4 pl-3">
                    <p class="headline primary--text">Payment settings</p>
                  </v-row>
                  <v-row class="mt-n3">
                    <v-col cols="12" md="6">
                      <v-text-field v-model="siteItem.onebrand_payment_api_endpoint" label="Onebrand payment API Endpoint"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n5">
                    <v-col cols="1">
                      <v-switch v-model="siteItem.bill_corporate_account" color="cyan darken-2" :disabled="isBillCorporateDisabled" class="pt-2"> </v-switch>
                    </v-col>
                    <v-col cols="10">
                      <v-container fill-height fluid class="pl-0">
                        <span class="subheading">Bill corporate account: </span>
                      </v-container>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n8">
                    <v-col cols="1">
                      <v-switch v-model="siteItem.charge_test_mode" color="cyan darken-2" :disabled="isCreditCardTurnedOff" class="pt-2"> </v-switch>
                    </v-col>
                    <v-col cols="10">
                      <v-container fill-height fluid class="pl-0">
                        <span span class="subheading">$1 Charge mode</span>
                      </v-container>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n8">
                    <v-col cols="1">
                      <v-switch v-model="siteItem.fifty_percent_payment_enabled" color="cyan darken-2" :disabled="isCreditCardTurnedOff" class="pt-2"> </v-switch>
                    </v-col>
                    <v-col cols="10">
                      <v-container fill-height fluid class="pl-0">
                        <span span class="subheading">50% Payment</span>
                      </v-container>
                    </v-col>
                    <v-alert v-if="!siteItem.credit_card_payment_enabled" class="mt-0" type="info" outlined color="#737373">
                      <span>Credit card payment is turned off</span>
                    </v-alert>
                  </v-row>
                  <v-row class="mt-n8">
                    <v-col cols="1">
                      <v-switch v-model="siteItem.cash_on_delivery_enabled" color="cyan darken-2" class="pt-2"> </v-switch>
                    </v-col>
                    <v-col cols="10">
                      <v-container fill-height fluid class="pl-0">
                        <span span class="subheading">Cash on Delivery</span>
                      </v-container>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n5">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.stripe_logo" label="Stripe logo"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="6">
                      <file-pond
                        ref="pond"
                        name="stripe_logo"
                        label-idle="Drop files here or Browse for Stripe logo"
                        accepted-file-types="image/jpeg, image/png"
                        :server="{ process }"
                        allowBrowse="true"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      <v-select v-model="siteItem.stripe_mode" :items="stripeTypes" item-text="text" item-value="value" label="Select Stripe Mode"> </v-select>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.stripe_live_key" label="Stripe Live Key"></v-text-field>
                      <v-text-field v-model="siteItem.stripe_test_key" label="Stripe Test Key"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.subscription_stripe_plan_live" label="Subscription Stripe Plan Live"></v-text-field>
                      <v-text-field v-model="siteItem.subscription_stripe_plan_test" label="Subscription Stripe Plan Test"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-4 pl-3">
                    <p class="headline primary--text">LDP Settings</p>
                  </v-row>
                  <v-row class="mt-n5">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.ldp_settings.ldp_contract_number_live" label="LDP - Valassis Contract Number LIVE" class="mb-n3"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.ldp_settings.ldp_contract_number_test" label="LDP - Valassis Contract Number TEST" class="mb-n3"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.ldp_settings.oracle_account_number" label="Oracle Account Number" class="mb-n3"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <template v-if="isMarkupPricesEnabled">
                    <v-row class="mt-4 pl-3">
                      <p class="headline primary--text">Markup Price Settings</p>
                    </v-row>
                    <v-row class="mt-4 pl-3">
                      <v-btn color="primary" small class="mb-2" @click="addNewMarkupPriceRow">
                        <v-icon left>mdi-plus</v-icon>
                        Add Row
                      </v-btn>
                    </v-row>

                    <v-row class="mt-4 pl-3">
                      <stamp-data-table v-model="markupPrices" :data="markupPrices" :headers="markupPriceHeaders" :is-footer-hidden="true" item-key="id">
                        <template #[`item.count`]="{ item }">
                          <div class="d-flex justify-center align-center py-2">
                            <v-text-field
                              v-model.number="item.min"
                              type="number"
                              dense
                              outlined
                              class="count-input text-center"
                              placeholder="Min"
                              hide-details
                              disabled
                              @blur="validateRange(item)"
                            ></v-text-field>
                            <span class="mx-3 subtitle-1">-</span>
                            <v-text-field
                              v-model.number="item.max"
                              type="number"
                              dense
                              outlined
                              class="count-input text-center"
                              placeholder="Max"
                              hide-details
                              :disabled="!item.max"
                              @blur="validateRange(item)"
                            ></v-text-field>
                          </div>
                        </template>
                        <template #[`item.markup`]="{ item }">
                          <v-text-field v-model.number="item.markup" prefix="%" type="number" :min="0" :max="100" dense outlined class="markup-input" hide-details></v-text-field>
                        </template>
                        <template #[`item.actions`]="{ item }">
                          <v-icon small :disabled="isDeleteMarkupRowDisabled" class="mr-2" @click="deleteMarkupRow(item)"> mdi-delete </v-icon>
                        </template>
                      </stamp-data-table>
                    </v-row>
                    <v-divider class="mt-6"></v-divider>
                  </template>

                  <FouroverDesignPrices @on-design-prices-changed="setDesignPrices" />

                  <v-row class="mt-n4">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.fourover_default_product_id" label="4over Product ID" class="mb-n3"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row class="mt-4 pl-3">
                    <p class="headline primary--text">Delivery</p>
                  </v-row>
                  <v-row>
                    <v-col cols="4" md="2">
                      <v-text-field v-model="siteItem.turnaround_days" type="number" label="Turnaround Days"></v-text-field>
                      <v-text-field v-model="siteItem.turnaround_rush_fee" type="number" step="0.1" label="Turnaround Rush Fee"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-row class="pl-3">
                        <v-switch v-model="siteItem.extra_drop_price_enabled" color="cyan darken-2" label="Extra drop price" @change="handleExtraDropPriceSwitchChange"> </v-switch>
                      </v-row>
                      <v-row class="mt-n12 pl-12">
                        <v-col cols="4" md="3">
                          <v-text-field v-model="siteItem.extra_drop_price" :disabled="!siteItem.extra_drop_price_enabled"></v-text-field>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-4 pl-3">
                    <p class="headline primary--text">Shipping</p>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="3">
                      <v-text-field v-model="siteItem.shipping.ship_from_zip" type="text" label="Ship From ZIP"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-4 pl-6">
                    <p class="subtitle primary--text">Markup Prices for Fedex Shipping Methods</p>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="3 pl-9">
                      <v-text-field v-model="siteItem.shipping.markup_prices.FIRST_OVERNIGHT" type="number" label="FedEx First Overnight®"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.PRIORITY_OVERNIGHT" type="number" label="FedEx Priority Overnight®"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.STANDARD_OVERNIGHT" type="number" label="FedEx Standard Overnight®"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_2_DAY_AM" type="number" label="FedEx 2Day® AM"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_2_DAY" type="number" label="FedEx 2Day®"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_EXPRESS_SAVER" type="number" label="FedEx Express Saver®"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_GROUND" type="number" label="FedEx Ground®"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_FIRST_FREIGHT" type="number" label="FedEx First Overnight® Freight"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_1_DAY_FREIGHT" type="number" label="FedEx 1Day® Freight"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_2_DAY_FREIGHT" type="number" label="FedEx 2Day® Freight"></v-text-field>
                      <v-text-field v-model="siteItem.shipping.markup_prices.FEDEX_3_DAY_FREIGHT" type="number" label="FedEx 3Day® Freight"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row class="mt-4 pl-6">
                    <p class="subtitle primary--text">Markup Price for 4Over Shipping</p>
                  </v-row>
                  <v-row class="mt-n4">
                    <v-col cols="3 pl-9">
                      <v-text-field v-model="siteItem.shipping.fourover_markup_price" prefix="%" type="number" min="0" label="4Over Markup"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row class="mt-n4">
                    <v-col cols="3">
                      <v-text-field v-model="siteItem.shipping.extra_qty_min_price" type="number" label="Minimum Price for Extra Quantity Shipping"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-4 pl-3">
                    <p class="headline primary--text">Incomplete Orders</p>
                  </v-row>
                  <v-row no-gutters class="mt-3">
                    <span class="title-1 mt-2">Aging Period (minutes)</span>
                    <v-col cols="3" md="1">
                      <v-text-field
                        v-model.number="siteItem.incomplete_order_aging_period"
                        :rules="[value => (!!value && value > 1) || 'Period has to be greater than 1']"
                        outlined
                        dense
                        type="number"
                        class="pl-4"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="12" md="6">
                      <v-textarea
                        v-model="incompleteOrderEmailAddresses"
                        outlined
                        rows="2"
                        label="Email Addresses"
                        hint="Please add a comma-separated email address list in case of multiple addresses"
                      ></v-textarea>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>

                  <v-row class="mt-4 pl-3">
                    <p class="headline primary--text">Customization</p>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="3">
                      <v-menu v-model="primaryColorMenu" :close-on-content-click="false" max-width="290">
                        <template v-slot:activator="{ on }">
                          <v-row class="ml-0">
                            <v-card class="colorBox" :color="siteItem.primary_color"></v-card>
                            <v-col cols="5" md="10">
                              <v-text-field
                                v-model="siteItem.primary_color"
                                label="Primary color"
                                class="mt-n7"
                                append-icon="mdi-close"
                                v-on="on"
                                @click:append="clearSearch('primary_color')"
                              >
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </template>
                        <v-color-picker v-model="primaryColor" show-swatches mode="hexa" @change="primaryColorMenu = false"></v-color-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-menu v-model="primaryHoverColorMenu" :close-on-content-click="false" max-width="290">
                        <template v-slot:activator="{ on }">
                          <v-row class="ml-0 justify-end">
                            <v-card class="colorBox" :color="siteItem.primary_hover_color"></v-card>
                            <v-col cols="5" md="10">
                              <v-text-field
                                v-model="siteItem.primary_hover_color"
                                label="Primary hover color"
                                class="mt-n7"
                                append-icon="mdi-close"
                                v-on="on"
                                @click:append="clearSearch('primary_hover')"
                              >
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </template>
                        <v-color-picker v-model="primaryHoverColor" show-swatches mode="hexa" @change="primaryHoverColorMenu = false"></v-color-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="3">
                      <v-menu v-model="secondaryColorMenu" :close-on-content-click="false" max-width="290">
                        <template v-slot:activator="{ on }">
                          <v-row class="ml-0">
                            <v-card class="colorBox" :color="siteItem.secondary_color"></v-card>
                            <v-col cols="5" md="10">
                              <v-text-field
                                v-model="siteItem.secondary_color"
                                label="Secondary color"
                                class="mt-n7"
                                append-icon="mdi-close"
                                v-on="on"
                                @click:append="clearSearch('secondary_color')"
                              >
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </template>
                        <v-color-picker v-model="secondaryColor" show-swatches mode="hexa" @change="secondaryColorMenu = false"></v-color-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-menu v-model="secondaryHoverColorMenu" :close-on-content-click="false" max-width="290">
                        <template v-slot:activator="{ on }">
                          <v-row class="ml-0 justify-end">
                            <v-card class="colorBox" :color="siteItem.secondary_hover_color"></v-card>
                            <v-col cols="5" md="10">
                              <v-text-field
                                v-model="siteItem.secondary_hover_color"
                                label="Secondary hover color"
                                class="mt-n7"
                                append-icon="mdi-close"
                                v-on="on"
                                @click:append="clearSearch('secondary_hover')"
                              >
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </template>
                        <v-color-picker v-model="secondaryHoverColor" show-swatches mode="hexa" @change="secondaryHoverColorMenu = false"></v-color-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n2">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.header_image_large" label="Header image large"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n6">
                    <v-col cols="6">
                      <file-pond
                        ref="pond"
                        name="header_image_large"
                        label-idle="Drop files here or Browse for Header Image Large"
                        accepted-file-types="image/jpeg, image/png"
                        :server="{ process }"
                        allowBrowse="true"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-n5">
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.header_image_small" label="Header image small"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n6">
                    <v-col cols="6">
                      <file-pond
                        ref="pond"
                        name="header_image_small"
                        label-idle="Drop files here or Browse for Header Image Small"
                        accepted-file-types="image/jpeg, image/png"
                        :server="{ process }"
                        allowBrowse="true"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field v-model="siteItem.order_page_hero_title" label="Order page hero title"></v-text-field>
                      <v-text-field v-model="siteItem.marketing_manager_email" label="Marketing manager email" :rules="emailRules"> </v-text-field>
                    </v-col>
                  </v-row>

                  <div class="form-group">
                    <p class="custom-paragraph mt-3">Order page hero text</p>
                    <tinymce
                      id="editorContent"
                      v-model="siteItem.order_page_hero_text"
                      :other_options="tinyOptions"
                      @editorInit="e => e.setContent(`${siteItem.order_page_hero_text}`)"
                    ></tinymce>
                  </div>
                  <div class="form-group">
                    <p class="custom-paragraph mt-3">Terms and Conditions</p>
                    <tinymce
                      id="terms_and_conditions"
                      v-model="siteItem.terms_and_conditions"
                      :other_options="tinyOptions"
                      @editorInit="e => e.setContent(`${siteItem.terms_and_conditions}`)"
                    ></tinymce>
                  </div>
                  <v-btn :disabled="!isValid" color="primary" class="mt-5" @click="submit"> <v-icon>mdi-floppy</v-icon>Save </v-btn>
                </v-form>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
        <!-- CUSTOM SETTINGS -->
        <v-card v-if="tab === 1" width="100%">
          <v-card-title class="mx-3 mb-4">
            <h2 class="my-2 headline primary--text">Custom settings</h2>
            <v-spacer></v-spacer>
            <v-btn color="primary" class="mt-1" @click="edit()"><v-icon class="pr-1">mdi-floppy</v-icon>Save</v-btn>
          </v-card-title>
          <v-card-text class="pl-8">
            <template v-for="(brandProp, index) in Object.entries(siteItem.customization)">
              <v-row :key="index" class="my-n6">
                <v-col cols="12" md="7" lg="6">
                  <v-text-field
                    v-if="brandProp[0] !== 'email_template' && brandProp[0] !== 'httpimg_custom_style'"
                    v-model="siteItem.customization[brandProp[0]]"
                    :label="brandProp[0] | beautifyText"
                    :value="brandProp[1]"
                    :rules="brandProp[0].includes('email') && brandProp[0] !== 'from_email_name' ? emailRules : []"
                  >
                  </v-text-field>
                </v-col>
                <v-col v-if="brandProp[0] === 'logo_url' && isImage(brandProp[1])" cols="12" md="3" lg="3" xl="3" class="offset-sm-1">
                  <v-img :src="logoSource" max-width="200px" alt="brandProp[0] | beautifyText"> </v-img>
                </v-col>
                <v-col v-if="brandProp[0] === 'email_template' || brandProp[0] === 'httpimg_custom_style'" cols="12" md="12" lg="9" xl="9" class="mb-8 mt-n5">
                  <label>{{ brandProp[0] | beautifyText }}</label>
                  <prism-editor v-model="siteItem.customization[brandProp[0]]" :code="brandProp[1]" language="html" autoStyleLineNumbers class="pr-5" />
                </v-col>
              </v-row>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" class="ml-7 mb-3" @click="edit()"><v-icon class="pr-1">mdi-floppy</v-icon>Save</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import tinymce from 'vue-tinymce-editor';
import PrismEditor from 'vue-prism-editor';
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import DataTable from '@/components/common/DataTable.vue';
import FouroverDesignPrices from '@/components/products/fourover/FouroverDesignPrices.vue';
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

export default {
  name: 'siteProcess',
  components: {
    FilePond,
    tinymce,
    PrismEditor,
    'stamp-data-table': DataTable,
    FouroverDesignPrices
  },
  filters: {
    beautifyText: function (text) {
      if (!text) return '';
      text = text.toLowerCase().toString();
      let formattedText = text.split('_');

      if (formattedText.length) {
        let finalText = '';
        for (let i = 0; i < formattedText.length; i++) {
          finalText = finalText + formattedText[i].charAt(0).toUpperCase() + formattedText[i].slice(1) + ' ';
        }
        return finalText.slice(0, -1);
      }
    }
  },
  data() {
    return {
      tinyOptions: {
        height: 200,
        statusbar: false,
        menubar: ''
      },
      incompleteOrderEmailAddresses: '',
      siteItem: {
        additional_services_available: false,
        bill_corporate_account: false,
        brand_web_id: '',
        cash_on_delivery_enabled: false,
        cancel_subscription_enabled: true,
        charge_test_mode: false,
        checkout_order_enabled: true,
        client_logo: '',
        contact_phone: '',
        created_at: '',
        crm_api_endpoint: '',
        credit_card_payment_enabled: true,
        custom_quote_url: '',
        design_live_asset_folder_path: '',
        download_emap_csv_enabled_on_site: false,
        edit_automated_mailer_enabled: true,
        extra_drop_price: 0,
        corporate_automated_mailer_enabled: false,
        env_type: null,
        is_product_from_db: false,
        fifty_percent_payment_enabled: true,
        finalize_design_enabled: false,
        fulfilment_location_enabled: false,
        enable_external_quote_saving: false,
        user_defined_fields_enabled: false,
        firebase_id: '',
        download_quote_enabled: false,
        header_image_large: '',
        header_image_small: '',
        httpimg_url: 'https://httpimg.firebaseapp.com/',
        incomplete_order_aging_period: 15,
        incomplete_order_email_addresses: [],
        is_edit_order_enabled_on_checkout: true,
        is_markup_prices_enabled: false,
        extra_drop_price_enabled: false,
        order_label_update_enabled: true,
        is_place_order_enabled: true,
        is_promo_enabled: true,
        ldp_settings: {
          ldp_contract_number_live: '',
          ldp_contract_number_test: '',
          oracle_account_number: ''
        },
        manual_discount_available: false,
        marketing_manager_email: '',
        multi_order_checkout_enabled: true,
        my_account_tabs: {
          orders_enabled: true,
          quotes_enabled: true,
          maps_enabled: true,
          designs_enabled: true,
          mailing_lists_enabled: true,
          legacy_orders_enabled: false,
          payment_methods_enabled: true,
          reports_enabled: false
        },
        onebrand_payment_api_endpoint: 'https://api.onebrand.io/payments/wp-payment',
        order_duplication_enabled: false,
        order_page_hero_text: '<p><strong>Enter text here</p>',
        order_page_hero_title: '',
        postage_auth_number_enabled: true,
        pricing_table_enabled: true,
        primary_color: '',
        primary_hover_color: '',
        re_order_enabled: true,
        register_user_fields: [],
        register_user_fields_enabled: false,
        salesforce_opportunity_enabled: false,
        secondary_color: '',
        secondary_hover_color: '',
        segment_write_key: '',
        sftp_upload_enabled: false,
        share_quote_enabled: true,
        sites_for_quote_saving_access: [],
        sites_for_share_quote: [],
        site_api_key: '',
        site_api_password: '',
        site_api_username: '',
        site_id: '',
        site_name: '',
        site_type: 'test',
        site_url: '',
        starting_order_with_map_enabled: true,
        stripe_live_key: '',
        stripe_logo: '',
        stripe_mode: 'test',
        stripe_test_key: 'pk_test_9ZfF0E0hFto4xgZhAphk5OVo',
        subscription_stripe_plan_live: '',
        subscription_stripe_plan_test: '',
        show_order_approval: false,
        terms_and_conditions:
          '<div class="col-md-12"> <div class="page-header"> <h1>Terms and Conditions</h1></div><p><strong>Terms and Conditions</strong></p><p><strong>&nbsp;</strong></p><p><strong>IMPORTANT TERMS AND CONDITIONS FOR PLACING AN ORDER WITH GrowMail (“we”, “us”, or “our”). By placing an order, you agree to the following terms and conditions (“Agreement”). <u>Please read them carefully before placing an order</u>.</strong></p><p><strong>&nbsp;</strong></p><ol><li><strong><u>SERVICES PROVIDED</u>: </strong>We provide graphic design, printing materials, mailing preparation, online advertising, and other marketing support services. We do not guarantee or represent that we will provide a return on investment, guaranteed income, leads, orders, customers, response rates, or any desired, expected, or intended results.</li><li><strong><u>TURNAROUND</u>: </strong>We use commercially reasonable efforts to meet your expectations and requests, including production and shipping dates. However, quoted and published ship or mail dates are best estimates and not to be considered guarantees.</li><li><strong><u>SCHEDULE</u>: </strong>Production turnaround time begins after you approve your proof. For proofs approved after 12:00 pm (Eastern Time), production turnaround time will begin the next business day. Business days are considered Monday through Friday. Holidays are excluded from business days.</li><li><strong><u>PAYMENT TERMS</u>: </strong>All orders must be prepaid before commencing production. We reserve the right to place jobs on indefinite hold if the credit card declines or for payment pending via check. There are no net credit terms accounts unless pre-approved in writing by management and secured by credit card or deposit. You authorize us to charge any credit card or debit card previously authorized for unpaid or overdue accounts not received within payment terms (if applicable).&nbsp; Additionally, you authorize us to charge any credit card or debit card previously used for nonsufficient funds (NSF or returned) checks. There is a fee of $35 for any nonsufficient funds checks.</li><li><strong><u>ALL SALES ARE FINAL</u>:</strong> Because each print job is created custom for you, it cannot be reused or resold. ALL SALES ARE FINAL.</li></ol><p>&nbsp;</p><ol><li><strong><u>ORDER CANCELLATIONS AND REFUNDS</u>: </strong>Credits or refunds for cancellation of orders are available on orders that have NOT been placed into production. If an order is cancelled AFTER we have sent a proof to you, but BEFORE it is approved by you, a refund will be provided to you less a $50 administrative fee for work completed prior to cancellation. If an order is cancelled AFTER a proof is approved by you but BEFORE it is placed into production, on written cancellation a refund will be given less a $100 fee for all work completed prior to cancellation. There are no refunds if an order is cancelled AFTER it has been placed into production.Orders with custom dies, third-party services (such as variable data postcards or post-press services), or special “non-returnable” paper stocks are not refundable after the order is placed.If you need to cancel an order placed, you must email the cancellation request to sales@growmail.com AND call and speak with our representative to confirm receipt of the cancellation and the cancellation charges at that point in time.&nbsp; Sending the email, or livechat, without confirmation is not enough to cancel an order.</li><li><strong><u>CLAIMS AND RETURNS</u>: </strong>Claims for defects, damages, or shortages must be timely made by you in writing no later than 10 calendar days after delivery. If no such claim is made, you agree the order and performance tendered by us have been accepted. If a job is unacceptable to you due to true defects in workmanship, and all the materials shipped are returned to us for inspection and confirmation of the defects, we will reprint the job at no cost, or issue a credit for future orders. No cash refunds are offered. Please note that the FULL original order must be returned to us and inspected.</li></ol><p>No reprints or credits are offered for the following reasons (these are not considered defects):</p><ul><li>Low resolution or low quality graphics and images as supplied by you</li><li>Spelling, punctuation, and grammatical errors from your approved proof</li><li>Damaged fonts</li><li>Transparency</li><li>Overprint<br>· Color reproduction</li><li>Artwork files that are not created following our or USPS specifications</li><li>Variances in color from the conversion of Pantone or RGB colors to CMYK</li><li>Errors in user-selected options such as size, quantity, paper and finish</li><li>Duplicate orders submitted by you</li><li>Incorrect files uploaded by you</li><li>Incorrect file orientation on your provided files.</li><li>Cracking on folds</li><li>Cutting variances</li><li>Incorrect or undeliverable shipping address</li></ul><p>&nbsp;</p><ol><li><strong><u>SHIPPING TERMS</u>:</strong> All shipments are FOB Shipping Point from our manufacturing location. We assume no liability for damage in shipping, missing, or otherwise delayed shipments.</li></ol><p>&nbsp;</p><ol><li><strong><u>COLOR REPRODUCTION</u>: </strong>Because of differences in equipment manufacturers, including paper, inks, lighting, and other conditions, all print jobs utilize a “pleasing color” spectrum as determined by generally accepted printing trade practices. The color output of your print job is not guaranteed to match any other print order or previous printed copy. We are not responsible for color variations between submitted files and the final printed piece. Under no circumstances will a reprint be honored for color variations that have occurred during the printing process.</li><li><strong><u>PROOFING PROCESS</u>:</strong> If you select “online proof” or request a digital proof, we will send an email notice shortly after receiving it into our production department.&nbsp; Please take note that an online proof is not an accurate color reproduction of the final printed piece but is a final opportunity for you to check the layout, bleeds, crops, and final text.&nbsp; The proofing process is very important to avoid any unintended issues.Approval of the correct and final print version is your sole responsibility. We are not responsible for any copy or design errors committed on edits once approved by you.</li><li><strong><u>COLOR PRINTER AND INKJET OR COLOR COPIES</u>:</strong> Inkjet and laser prints will look substantially different than commercial offset printing. We offer quick turnaround and low prices by printing to a “pleasing color” standard, using standard ink densities. There is no guarantee that your finished piece will match its printed sample. This is due to the varying results from different output devices including inkjet printers, continuous tone proofing devices, and film-based proofs. Consumer printers are known to saturate colors and glossy “photo” paper will look different.</li><li><strong><u>CONTENT</u>: </strong>We reserve the right, in our sole discretion, to: i) refuse to provide service to you at any time and for any reason; or ii) print anything we deem improper, offensive, or known to be illegal. You guarantee that you have the legal authority to allow us to use all matter submitted to us by you, including but not limited to the necessary license and permission to use any copyrighted material, trademarks, or graphics. You are solely responsible for the content of all matter submitted to us by you. We are not liable for any damages resulting from violation of copyright laws or illegal use of trade names or slogans.</li></ol><p>&nbsp;</p><ol><li><strong><u>QUOTES AND ESTIMATES</u>: </strong>Written estimates are good for 30 days. An estimate not accepted within 30 days may be subject to change. Prices posted on our website are subject to change without notice. Quotations are based on the accuracy and completeness of the specifications and selections provided by you in writing. We reserve the right to modify a prior quotation made if, at the time of submission of the order, the art or other parameter does not conform to the information on which the original estimate was based.</li><li><strong><u>OVERRUN/UNDERRUN</u>:</strong> We will normally deliver the exact quantity of goods ordered plus a small additional amount. There is no extra charge for the additional amount. Should an order be delivered with less than the amount ordered, you agree that a credit issued for the value of the shorted pieces is acceptable as full satisfaction owed for the shorted items.</li><li><strong><u>CUSTOMER FILES</u>: </strong>It is your responsibility to maintain a copy of the original computer files, artwork. We are not responsible for accidental loss or damage to media supplied by you or for errors on supplied artwork furnished by you. We do not always archive your work for longer than it takes to produce the final product. DO NOT send any “one-of-kind” prints or artwork.</li><li><strong><u>STOCK PHOTOGRAPHY LICENSE</u>: </strong>All materials we create in producing printed materials or postcards are the property of us and/or our affiliates or licensors, and are protected by copyright, trademark, and other intellectual property laws. This includes typeset layouts and color scans. We may use licensed stock images for customer designs, under our corporate license. You may not re-use the material that we create for any other purpose other than the printed piece provided by us.</li><li><strong><u>USPS DISCLAIMER</u>:</strong> We provide design, printing service and mailing preparation services. We DO NOT provide mail delivery services.&nbsp; This service is provided by United States Postal Service (“USPS”). We are not USPS and cannot guarantee the delivery schedules or in-home dates of your mailings. Our services are completed when your mail is delivered to the dock of the USPS. If there is a delivery or service issue specific to USPS, we will assist you in coordinating all available resolutions with USPS directly. USPS will require specific and/or verifiable proof of error. A self-certified statement that a postcard was not received is <u>not</u> a verifiable error for USPS.</li><li><strong><u>DISTRIBUTION VERIFICATION</u>: </strong>Upon request by you within 30 days of the in-home date of your mailing, we will provide you verification that your mail was deposited with the USPS.</li></ol><p>&nbsp;</p><ol><li><strong><u>MAILING LIST QUALITY</u>:</strong> We are not responsible for undeliverable or returned pieces on any supplied mailing list data provided by you regardless if we processed NCOA and CASS certifications.</li><li><strong><u>PURCHASED MAILING LISTS</u>:</strong> Mailing lists which are purchased from us will not have returns on consumer lists exceeding 8% and on business lists exceeding 15% (“Return Levels”), conditional on the mailing being sent within 30 days of the purchase of the lists. We are not liable for any returns other than those in excess of the Return Levels. The sole remedy for exceeding the Return Levels shall be refund of the postage of those items that exceed the Return Levels.Due to contractual terms with third-party mailing data providers, we are unable to refund on mailing list purchases once procured regardless if the list has been mailed or used in any capacity by you.</li></ol><p>&nbsp;</p><ol><li><strong><u>POSTAGE FEES</u></strong>: You should explicitly recognize that we are not the USPS and any money paid for postage is collected and paid to USPS on your behalf. These funds are not ours and are not refundable for any reason by us. To allow sufficient time for payment deposit with the USPS, when you use our permit number, we require that all checks and credit card payments for postage be in our possession 3 days before your mail date. If a postage deposit is not received, we cannot advance the postage required, and the USPS will not accept your mail.</li><li><strong><u>POSTAGE REFUNDS</u>: </strong>UNDER ABSOLUTELY NO CIRCUMSTANCES WILL POSTAGE BE REFUNDED FOR ANY REASON ONCE AN ORDER HAS BEEN TRANSFERRED TO THE USPS FOR MAILING.</li><li><strong><u>STORAGE CHARGES</u>: </strong>Material held more than 90 days after production is completed will be billed at a charge of $5 per 1,000 pieces per month. If we are unable to contact you within 60 days, we will consider the material abandoned and it will be recycled.</li></ol><p>&nbsp;</p><ol><li><strong><u>DETACHED ADDRESS LABEL GEOGRAPHY</u>:</strong> We will make commercially reasonable efforts to ensure your requested geography for any detached address label postcard campaign is met, provided however, certain geographies and services may not be available for all requested mail dates. In such circumstances, we may substitute those requested geographies with available ones within a reasonable distance from the requested geography to meet your specific quantity. If the quantity changes due to these substitutions your total cost will be adjusted accordingly.</li></ol><p>&nbsp;</p><ol start="25"><li><strong><u>WARRANTY AND DISCLAIMER OF OTHER WARRANTY</u>:</strong> We make no warranties, express or implied, with respect to results you may obtain through use of our services. We intend to use commercially reasonable efforts to provide services in accordance with this Agreement; however, it is possible for errors, omissions or other mishaps to occur. We will have no liability in such case. IT IS EXPRESSLY AGREED THAT THIS WARRANTY IS IN LIEU OF ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTY OF WARRANTY OF MERCHANTABILITY AND WARRANTY OF FITNESS FOR A PARTICULAR PURPOSE. WITHOUT LIMITING THE GENERALITY OF THE FOREGOING, WE DO NOT WARRANT ANY RESULTS YOU MAY OBTAIN THROUGH SERVICES SUPPLIED BY US.</li></ol><p>&nbsp;</p><ol><li><strong><u>LIMITATIONS OF LIABILITY</u>: </strong>WE WILL NOT BE LIABLE FOR: I) ANY DIRECT DAMAGES RELATING TO ERROR, MISTAKE, OR FAILURE TO PERFORM SERVICES OF ANY NATURE THAT EXCEEDS THE VALUE OF THE FEES PAID BY YOU TO US FOR THE SPECIFIC SERVICE GIVING RISE TO THE DAMAGES; II) ANY INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF BUSINESS, LOSS OF PROFITS, FAILURE TO OBTAIN CERTAIN BUSINESS RESULTS, RESPONSE RATES, SALES, POSTAGE OR ANY OTHER DIRECT OR CONSEQUENTIAL DAMAGES AS A RESULT OF ANY ERROR, MISTAKE, OR FAILURE OF US TO PERFORM SERVICES. THIS INCLUDES BUT IS NOT LIMITED TO; LIST ADDITIONS, REQUESTS MADE AFTER ORDER APPROVAL, AND USPS ERRORS.&nbsp; WE ARE NOT LIABLE IN ANY MANNER OR AMOUNT FOR USPS PERFORMANCE FAILURES, DAMAGE, OR DELAYS AND MAKE NO WARRANTY WHATSOEVER REGARDING USPS PERFORMANCE.</li></ol><p>&nbsp;</p><ol><li><strong><u>INDEMNIFICATION</u>: </strong>You represent that you have the legal right to produce all printed materials ordered. In the event that a charge, claim or demand, or arbitration, action or proceeding (collectively, a “Claim”) is made or commenced against us based upon, relating to or arising from your acts, or the printing performed or product produced by us ordered by you, you shall indemnify and hold us harmless from and against any loss, damages, cost and expense arising from or related to the Claim including without limitation; (1) defending us against any such Claim (2) paying any judgment or award against us; and (3) reimbursing us for any legal fees and expenses we reasonably incur in responding and defending the Claim.</li></ol><p><strong>&nbsp;</strong></p><ol><li><strong><u>PRIVACY POLICY AND ACCEPTABLE USE POLICY</u>:</strong> You agree to our Privacy Policy and Acceptable Use Policy, which are incorporated by reference into this Agreement.</li><li><strong><u>DATA SECURITY AND MALWARE POLICY / DISCLAIMER OF WARRANTY</u>: </strong>You agree and acknowledges the following: (a) that we do not warrant that any of our websites or vendor websites or electronic services will be uninterrupted, error free, or immune from hackers that create data breaches and data theft, and further that we, and our various third-party providers (such as Amazon) may occasionally experience “hard outages” due to internet disruptions, or data security breaches. WE EXPRESSLY DISCLAIMS ANY WARRANTY OR ASSURANCE OF DATA INTEGRITY, SECURITY, OR PERFORMANCE. Any such breaches or outages shall not be considered a breach of these terms and conditions. Further, we agree to use reasonable commercial efforts to ensure that there are no viruses or undocumented feature in any software, email, or data files at the time of delivery to you &nbsp;(a virus will be defined as any harmful, hidden programs or data incorporated in a computer software program that destroys or impairs the program and/or data from processing its normal business operations); and the software does not contain any embedded device or code (e.g., time bomb), but we MAKE NO WARRANTY EXPRESS OR IMPLIED THAT MALICIOUS CODE WILL NOT BE PRESENT ON ANY CUSTOMER COMMUNICATION OR DATA FILES.</li><li><strong><u>GOVERNING LAW AND VENUE</u>: </strong>This Agreement is entered into in Lancaster County, Pennsylvania, and all parties hereto consent to personal jurisdiction in the courts of the Commonwealth of Pennsylvania sitting in Lancaster County, for all actions arising under or in connection with these Agreements. This Agreement shall be governed and construed according to the laws of New York without regard to its conflict of law’s provisions.</li></ol><p><strong>&nbsp;</strong></p><ol><li><strong><u>SEVERABILITY</u>:</strong> If any provision of this Agreement or the application thereof shall, for any reason and to any extent, be invalid or unenforceable, neither the remainder of this Agreement nor the application of the provision to other persons, entities or circumstances shall be affected thereby, but instead shall be enforced to the maximum extent permitted by law.</li></ol><p><strong>&nbsp;</strong></p><ol><li><strong><u>FORCE MAJEURE</u>: </strong>In no event shall we be responsible or liable for any failure or delay in the performance of its obligations hereunder arising out of or caused by, directly or indirectly, forces beyond our control, including, without limitation, hurricanes, tropical storms or threat of same, strikes, work stoppages, accidents, acts or threats of war or terrorism, civil or military disturbances, nuclear or natural catastrophes or acts of God, and interruptions, loss or malfunctions of utilities, communications or computer (software and hardware) services, it being understood that we shall use reasonable best efforts which are consistent with accepted practices in the printing industry to resume performance as soon as practicable under the circumstances.</li><li><strong><u>AGREEMENT CHANGES</u>:</strong> We may change, modify, add or remove portions of this Agreement at any time, and any changes will become effective immediately upon being posted. This Agreement was last updated on October 2, 2018.</li></ol><p>&nbsp;</p><p>END of TERMS AND CONDITIONS: October 2, 2018</p></div>',
        turnaround_days: 7,
        turnaround_rush_fee: 0,
        shipping: {
          ship_from_zip: '34689',
          markup_prices: {
            FIRST_OVERNIGHT: 15,
            PRIORITY_OVERNIGHT: 15,
            STANDARD_OVERNIGHT: 15,
            FEDEX_2_DAY_AM: 15,
            FEDEX_2_DAY: 15,
            FEDEX_EXPRESS_SAVER: 15,
            FEDEX_GROUND: 15,
            FEDEX_FIRST_FREIGHT: 15,
            FEDEX_1_DAY_FREIGHT: 15,
            FEDEX_2_DAY_FREIGHT: 15,
            FEDEX_3_DAY_FREIGHT: 15
          },
          fourover_markup_price: 0,
          extra_qty_min_price: 16
        },
        user_defined_fields: [],
        customization: {
          account_url: '',
          address: '',
          base_url: '',
          company_name: '',
          customer_service_email: '',
          customer_service_tollfree: '',
          design_dept_email: '',
          domain: '',
          eddm_route_email: '',
          email_template: '',
          from_email_address: '',
          from_email_name: '',
          httpimg_style_url: '',
          incomplete_url: '/',
          list_dept_email: '',
          location_url: '/',
          logo_url: '',
          mail_dept_email: '',
          my_account: '/',
          order_detail_url: '',
          ordertracking_url: '/',
          quote_approve_url: '/',
          quote_detail_url: '/',
          referral_url: '/',
          review_proofs_link: '/',
          review_proofs_url: '/',
          web_code: ''
        },
        markup_prices: []
      },
      isValid: false,
      siteTypes: [
        { text: 'Live', value: 'live' },
        { text: 'Test', value: 'test' }
      ],
      stripeTypes: [
        { text: 'Live', value: 'live' },
        { text: 'Test', value: 'test' }
      ],
      isCampSite: false,
      firebaseSiteKey: '',
      siteUpdateAlert: false,
      description: '',
      isSuccessDialogOpened: false,
      // Rules
      siteIdRules: [v => !!v || 'Site Id is required'],
      BrandWebIdRules: [v => !!v || 'Brand web ID is required'],
      SiteNameIdRules: [v => !!v || 'Site name is required'],
      SiteUrlRules: [v => !!v || 'Site URL is required'],
      SiteAPiKeyRules: [v => !!v || 'Site API Key is required'],
      DesignLiveRules: [v => !!v || 'Design live asset Folder Path is required'],
      emailRules: [v => /.+@.+/.test(v) || 'E-mail must be valid'],
      oboppSites: [],
      autocompleteErrorMessage: '',
      fieldCount: 7,
      testSites: ['-LGjZtGpLePH-61Rp662', '-Lu7t90MWKHODwcW_pxW'],
      new_user_defined_fields: [
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_1',
          field_is_required: false
        },
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_2',
          field_is_required: false
        },
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_3',
          field_is_required: false
        },
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_4',
          field_is_required: false
        },
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_5',
          field_is_required: false
        },
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_6',
          field_is_required: false
        },
        {
          field_name: '',
          field_label: '',
          field_description: '',
          field_type: 'text',
          field_id: 'order_field_7',
          field_is_required: false
        }
      ],
      tab: null,
      logoSource: '',
      primaryColorMenu: false,
      primaryHoverColorMenu: false,
      secondaryColorMenu: false,
      secondaryHoverColorMenu: false,
      primaryColor: '',
      primaryHoverColor: '',
      secondaryColor: '',
      secondaryHoverColor: '',
      markupPriceHeaders: [
        { text: 'Count', value: 'count', sortable: false, width: '300' },
        { text: 'Markup', value: 'markup', sortable: false, align: 'center' },
        { text: '', value: 'actions', sortable: false, align: 'center' }
      ],
      markupPrices: this.siteItem?.markup_prices?.length ?? [
        { min: 0, max: 1000, markup: 55 },
        { min: 1001, max: 5000, markup: 50 },
        { min: 5001, max: 10000, markup: 45 },
        { min: 10001, max: 25000, markup: 40 },
        { min: 25001, max: 50000, markup: 38 },
        { min: 50001, max: 100000, markup: 37 },
        { min: 100001, markup: 35 }
      ],
      designPrices: []
    };
  },
  computed: {
    isOpenedToEdit() {
      return !!this.firebaseSiteKey;
    },
    isDuplicate() {
      return this.duplicateSite !== null;
    },
    activeText() {
      return this.siteItem?.env_type || 'amp';
    },
    productSourceText() {
      return `Product Source: ${this.siteItem?.is_product_from_db ? 'Firestore' : 'Wordpress'}`;
    },
    siteSelect() {
      return this.isCampSite;
    },
    ...mapGetters('site', {
      siteKey: 'getSiteKey',
      duplicateSite: 'getDuplicateSite',
      sites: 'getSites'
    }),
    siteFirebaseIdSelect() {
      return this.siteItem.sites_for_quote_saving_access;
    },
    availableSitesForShare() {
      return this.sites?.filter(site => site?.firebaseId !== this.firebaseSiteKey);
    },
    isPricingWarningMessageVisible() {
      return !this.siteItem.pricing_table_enabled && this.siteItem.re_order_enabled;
    },
    isCreditCardTurnedOff() {
      return !this.siteItem.credit_card_payment_enabled;
    },
    isCreditCardDisabled() {
      return !this.siteItem.bill_corporate_account && !this.isCampSite;
    },
    isBillCorporateTurnedOff() {
      return !this.siteItem.bill_corporate_account;
    },
    isBillCorporateDisabled() {
      return this.isCampSite || this.isCreditCardTurnedOff;
    },
    isMarkupPricesEnabled() {
      return this.siteItem?.is_markup_prices_enabled || false;
    },
    selectedSite() {
      return this.$route.params.id;
    },
    isDeleteMarkupRowDisabled() {
      return this.markupPrices.length === 1;
    }
  },
  watch: {
    siteSelect(value) {
      this.siteItem.env_type = this.isCampSite ? 'obopp' : 'amp';
      if (this.isCampSite) {
        this.disableBillCorporate();
      }
    },
    siteFirebaseIdSelect(value) {
      if (value.length > 0) {
        this.isValid = true;
        this.autocompleteErrorMessage = '';
      }
    },
    primaryColor(value) {
      this.siteItem.primary_color = value;
    },
    primaryHoverColor(value) {
      this.siteItem.primary_hover_color = value;
    },
    secondaryColor(value) {
      this.siteItem.secondary_color = value;
    },
    secondaryHoverColor(value) {
      this.siteItem.secondary_hover_color = value;
    },
    markupPrices: {
      handler(value) {
        this.siteItem.markup_prices = value;
      },
      deep: true
    }
  },
  async created() {
    this.$store.dispatch('showLoader', { message: 'Loading, please stand by...' });
    this.firebaseSiteKey = this.$route.params?.id ?? '';

    this.siteItem.user_defined_fields = this.new_user_defined_fields;

    this.$store.commit('site/setSitesAcceptedRoutes', this.$options.name);

    this.oboppSites = await this.$store.dispatch('site/listObbopSites');

    if (this.firebaseSiteKey) {
      const siteData = await this.$store.dispatch('site/listSelectedSite', { siteKey: this.firebaseSiteKey });
      console.log('[ Site data ]:', siteData);

      this.primaryColor = siteData?.primary_color || '';
      this.primaryHoverColor = siteData?.primary_hover_color || '';
      this.secondaryColor = siteData?.secondary_color || '';
      this.secondaryHoverColor = siteData?.secondary_hover_color || '';
      this.markupPrices = siteData?.markup_prices ?? this.markupPrices;

      siteData.user_defined_fields = siteData?.user_defined_fields?.length ? siteData.user_defined_fields : this.new_user_defined_fields;

      siteData.shipping.extra_qty_min_price = siteData?.shipping?.extra_qty_min_price ?? 16;

      Object.assign(this.siteItem, siteData);

      if (this.siteItem.user_defined_fields.length < 7) {
        this.siteItem.user_defined_fields = this.addEmtyUserDefinedFields(this.siteItem.user_defined_fields);
      }

      if (this.siteItem.incomplete_order_email_addresses) {
        Object.values(
          this.siteItem.incomplete_order_email_addresses.map(
            (item, index) => (this.incompleteOrderEmailAddresses += `${item}${index + 1 < Object.values(this.siteItem.incomplete_order_email_addresses).length ? ', ' : ''}`)
          )
        );
      }
    }

    if (this.duplicateSite) Object.assign(this.siteItem, this.duplicateSite);

    this.isCampSite = this.siteItem?.env_type === 'obopp';
    if (this.isCampSite) {
      this.disableBillCorporate();
    }

    if (!this.sites) this.$store.dispatch('loadSitesFromDb');
    this.$store.dispatch('hideLoader');
  },
  destroyed() {
    this.$store.commit('site/setDuplicateSite', null);
  },
  methods: {
    addEmtyUserDefinedFields(userDefinedFields) {
      const defaultTemplate = {
        field_name: '',
        field_label: '',
        field_description: '',
        field_type: 'text',
        field_is_required: false
      };

      for (let i = userDefinedFields.length + 1; i <= 7; i++) {
        let newField = { ...defaultTemplate, field_id: `order_field_${i}` };
        userDefinedFields.push(newField);
      }

      return userDefinedFields;
    },
    back() {
      this.$router.back();
    },
    onCreditCardPaymentChanged() {
      if (!this.siteItem.credit_card_payment_enabled) {
        this.siteItem.charge_test_mode = false;
        this.siteItem.fifty_percent_payment_enabled = false;
      }
    },
    submit() {
      if (this.$refs.form.validate()) {
        this.formatSharedPagesArray();
        console.log('[ Site Data ]:', this.siteItem);
        this.siteItem.shipping.markup_prices = this.parsePricesToFloat();

        if (this.siteItem.enable_external_quote_saving && !this.siteItem.sites_for_quote_saving_access?.length) {
          this.autocompleteErrorMessage = 'Obbop selected site list must be filled';
          this.isValid = true;
          return;
        }

        if (!this.siteItem.register_user_fields_enabled) {
          this.siteItem.register_user_fields = [];
        }
        if (!this.siteItem.user_defined_fields_enabled) {
          this.siteItem.user_defined_fields = [];
        }
        if (this.siteItem.is_markup_prices_enabled) {
          this.siteItem.markup_prices = this.markupPrices;
        }
        this.siteItem.register_user_fields = this.siteItem.register_user_fields.filter(x => x);

        if (this.isOpenedToEdit) {
          if (!this.siteItem.enable_external_quote_saving) {
            this.siteItem.sites_for_quote_saving_access = [];
          }
          this.edit();
        } else {
          this.save();
        }
      }
    },
    save() {
      this.siteItem.turnaround_days = parseInt(this.siteItem.turnaround_days);
      this.siteItem.turnaround_rush_fee = parseFloat(this.siteItem.turnaround_rush_fee);
      this.siteItem.created_at = Date.now();
      this.siteItem.firebase_id = '';

      this.$store
        .dispatch('site/createNewSite', {
          siteItem: this.siteItem,
          duplicate: this.isDuplicate,
          duplicateSite: this.duplicateSite
        })
        .then(() => {
          this.isCampSite = false;
          this.isSuccessDialogOpened = true;
          this.$emit('onSiteSaved');
        });
    },
    async edit() {
      const splittedEmailAddresses = this.incompleteOrderEmailAddresses ? this.incompleteOrderEmailAddresses.split(',').map(address => address.replace(' ', '')) : [];
      this.siteItem.incomplete_order_email_addresses = splittedEmailAddresses;

      Object.entries(this.siteItem).forEach(([key, value]) => {
        if (/^\d+$/.test(value)) {
          this.siteItem[key] = parseFloat(value);
        }
      });

      Object.entries(this.siteItem.customization).forEach(([key, value]) => {
        if (/^\d+$/.test(value)) {
          this.siteItem['customization'][key] = parseFloat(value);
        }
      });
      console.log('Edit site: ', this.siteItem);

      await this.$store.dispatch('product_pricing/updateFouroverDesignPrices', {
        prices: this.designPrices,
        site_id: this.selectedSite
      });

      await this.$store
        .dispatch('site/editSite', {
          siteItem: this.siteItem,
          siteKey: this.firebaseSiteKey
        })
        .then(data => {
          this.siteUpdateAlert = true;
          this.isCampSite = data === 'obopp';
          this.scrollToTop();
          this.$emit('onSiteSaved');
        })
        .catch(error => {
          console.log('Editing site settings has failed: ', error);
        });
    },
    close() {
      this.$router.back();
    },
    scrollToTop() {
      var element = document.getElementById('top-page');
      var top = element.offsetTop;
      window.scrollTo(0, top);
    },
    process(fieldName, file, metadata, load, error, progress, abort) {
      this.$store
        .dispatch('site/addImageToStorage', {
          file: file,
          fieldName: fieldName,
          time: Date.now()
        })
        .then(data => {
          load();
          this.siteItem[data.fieldName] = data.downloadURL;
        })
        .catch(error => {
          console.error('Error vue: ', error);
          abort();
        });
    },
    addItemToUserDefineOrderField() {
      this.fieldCount = this.fieldCount + 1;
    },
    formatDate(dateTotransform) {
      return dateTotransform ? this.$moment(new Date(parseInt(dateTotransform))).format('MMMM DD, YYYY [at] HH:mm:ss') : '';
    },
    formatSharedPagesArray() {
      this.siteItem.sites_for_share_quote = this.siteItem?.sites_for_share_quote?.map(({ firebase_id, site_id, env_type, site_name }) => {
        return { firebase_id, site_id, env_type, site_name };
      });
    },
    isImage(value) {
      const item = value.toString();
      if (item.includes('.png') || item.includes('.jpg') || item.includes('.jpeg') || item.includes('.svg')) {
        if (!item.includes('http') && !item.includes('https')) {
          this.logoSource = 'http://' + item;
        } else {
          this.logoSource = item;
        }
      }
      return item.includes('.png') || item.includes('.jpg') || item.includes('.jpeg') || item.includes('.svg');
    },
    destroy() {
      tinymce.execCommand('mceRemoveEditor', true, 'editorContent');
      tinymce.execCommand('mceRemoveEditor', true, 'terms_and_conditions');
    },
    clearSearch(target) {
      if (target === 'primary_color') {
        this.siteItem.primary_color ? (this.siteItem.primary_color = '') : null;
      } else if (target === 'primary_hover') {
        this.siteItem.primary_hover_color ? (this.siteItem.primary_hover_color = '') : null;
      } else if (target === 'secondary_color') {
        this.siteItem.secondary_color ? (this.siteItem.secondary_color = '') : null;
      } else if (target === 'secondary_hover') {
        this.siteItem.secondary_hover_color ? (this.siteItem.secondary_hover_color = '') : null;
      }
    },
    removeLastWord(text) {
      return text.substring(0, text.lastIndexOf('_'));
    },
    handleExtraDropPriceSwitchChange() {
      this.siteItem.extra_drop_price = this.siteItem.extra_drop_price_enabled ? 25 : 0;
    },
    disableBillCorporate() {
      this.siteItem.bill_corporate_account = false;
    },
    onManageProducts() {
      this.$router.push({ path: `/products/sites/${this.selectedSite}` });
    },
    parsePricesToFloat() {
      return Object.fromEntries(Object.entries(this.siteItem.shipping.markup_prices).map(([key, value]) => [key, parseFloat(value)]));
    },
    deleteMarkupRow(item) {
      const index = this.markupPrices.findIndex(row => row.max === item.max && row.min === item.min);

      if (index > -1) {
        this.markupPrices.splice(index, 1);
      }

      if (!this.markupPrices.length) {
        return;
      }

      if (index === 0) {
        this.markupPrices[0].min = 0;
      }

      if (index === this.markupPrices.length) {
        delete this.markupPrices[this.markupPrices.length - 1].max;
      }

      this.validateRanges();
    },
    addNewMarkupPriceRow() {
      const lastRow = this.markupPrices[this.markupPrices.length - 1];
      const lastRowIndex = this.markupPrices.length - 1;

      if (lastRowIndex >= 0) {
        if (lastRow.min && !lastRow.max) {
          lastRow.max = lastRow.min * 2 - 1;
        }
        if (!lastRow.min && !lastRow.max) {
          lastRow.max = 999;
        }

        this.markupPrices.push({
          min: this.markupPrices[this.markupPrices.length - 1].max + 1,
          markup: this.markupPrices[this.markupPrices.length - 1].markup
        });
      } else {
        this.markupPrices.push({
          min: 0,
          markup: 55
        });
      }
    },

    validateRange(item) {
      if (!this.markupPrices.length) return;

      const index = this.markupPrices.indexOf(item);
      const isFirst = index === 0;
      const isLast = index === this.markupPrices.length - 1;

      if (isFirst) {
        item.min = 0;
      }

      if (item.max && item.max <= item.min) {
        item.max = item.min + 1;
      }

      if (isLast && item.max) {
        delete item.max;
        return;
      }

      const prev = !isFirst ? this.markupPrices[index - 1] : null;
      const next = !isLast ? this.markupPrices[index + 1] : null;

      if (item.min && !item.max) {
        item.max = item.min + 1;
      }

      if (prev?.max && item.min !== prev.max + 1) {
        item.min = prev.max + 1;
      }
      if (next && item.max && next.min !== item.max + 1) {
        next.min = item.max + 1;
      }

      this.validateRanges();
    },
    validateRanges() {
      this.markupPrices.forEach((item, index) => {
        const isFirst = index === 0;
        const isLast = index === this.markupPrices.length - 1;
        const prev = !isFirst ? this.markupPrices[index - 1] : null;
        const next = !isLast ? this.markupPrices[index + 1] : null;

        if (isFirst) {
          item.min = 0;
        }

        if (isLast) {
          delete item.max;
        } else if (item.min && !item.max) {
          item.max = item.min + 1;
        }

        if (prev?.max && item.min !== prev.max + 1) {
          item.min = prev.max + 1;
        }

        if (next && item.max) {
          next.min = item.max + 1;
        }

        if (item.max && item.min >= item.max) {
          item.max = item.min + 1;
        }
      });
    },
    setDesignPrices(prices) {
      this.designPrices = prices;
    }
  }
};
</script>

<style scoped>
.custom-paragraph {
  font-size: 16px;
  color: #9e9e9e;
}
.custom-button {
  border: solid red 1px;
}
.colorBox {
  width: 30px;
  height: 30px;
  border: 1px solid grey;
}
.v-input .v-input--is-label-active .v-input--switch {
  color: blue !important;
}
</style>
